/**
 * Fail-closed effective state for catalog taxonomy nodes.
 * Only strict === true counts as active / in-navigation.
 */

export const INHERITED_STATUS_LABELS = {
  self_archived: 'В архіві',
  ancestor_archived: 'Недоступно через архів батьківської категорії',
  self_inactive: 'Неактивний',
  ancestor_inactive: 'Недоступно через неактивну батьківську категорію',
  self_hidden_nav: 'Прихований у навігації',
  ancestor_hidden_nav: 'Прихований через батьківську навігацію',
  ok: null,
}

export const INHERITED_STATUS_COLORS = {
  self_archived: 'var(--darkGray)',
  ancestor_archived: 'var(--darkGray)',
  self_inactive: '#e74c3c',
  ancestor_inactive: '#e74c3c',
  self_hidden_nav: '#3498db',
  ancestor_hidden_nav: '#3498db',
}

/**
 * @param {object} node
 * @param {object[]} [ancestors] root → parent
 */
export function computeEffectiveState(node, ancestors = []) {
  const list = Array.isArray(ancestors) ? ancestors : []

  const effectiveArchived =
    node?.isArchived === true || list.some((ancestor) => ancestor?.isArchived === true)

  const effectiveActive =
    !effectiveArchived &&
    node?.isActive === true &&
    list.every((ancestor) => ancestor?.isActive === true)

  const effectiveNavigation =
    effectiveActive &&
    node?.showInNavigation === true &&
    list.every((ancestor) => ancestor?.showInNavigation === true)

  return {
    effectiveArchived,
    effectiveActive,
    effectiveNavigation,
  }
}

/**
 * First matching inherited/self reason for UI badges.
 * @returns {'self_archived'|'ancestor_archived'|'self_inactive'|'ancestor_inactive'|'self_hidden_nav'|'ancestor_hidden_nav'|'ok'}
 */
export function resolveInheritedStatusReason(node, ancestors = []) {
  const list = Array.isArray(ancestors) ? ancestors : []

  if (node?.isArchived === true) return 'self_archived'
  if (list.some((a) => a?.isArchived === true)) return 'ancestor_archived'
  if (node?.isActive !== true) return 'self_inactive'
  if (list.some((a) => a?.isActive !== true)) return 'ancestor_inactive'
  if (node?.showInNavigation !== true) return 'self_hidden_nav'
  if (list.some((a) => a?.showInNavigation !== true)) return 'ancestor_hidden_nav'
  return 'ok'
}

/**
 * Status lines for list/editor: self markers + at most one inherited (archive/inactive).
 * Hidden-nav ancestor is editor-oriented; list shows self hidden-nav only.
 */
export function getCatalogTaxonomyEffectiveStatusLines(node, ancestors = [], { includeAncestorHiddenNav = false } = {}) {
  if (!node) return []

  if (node.isArchived === true) {
    return [
      {
        reason: 'self_archived',
        label: INHERITED_STATUS_LABELS.self_archived,
        markColor: INHERITED_STATUS_COLORS.self_archived,
      },
    ]
  }

  const lines = []
  const ancestorArchived = ancestors.some((a) => a?.isArchived === true)
  const ancestorInactive = ancestors.some((a) => a?.isActive !== true)
  const ancestorHiddenNav = ancestors.some((a) => a?.showInNavigation !== true)

  if (ancestorArchived) {
    lines.push({
      reason: 'ancestor_archived',
      label: INHERITED_STATUS_LABELS.ancestor_archived,
      markColor: INHERITED_STATUS_COLORS.ancestor_archived,
    })
  } else if (ancestorInactive) {
    lines.push({
      reason: 'ancestor_inactive',
      label: INHERITED_STATUS_LABELS.ancestor_inactive,
      markColor: INHERITED_STATUS_COLORS.ancestor_inactive,
    })
  } else if (includeAncestorHiddenNav && ancestorHiddenNav) {
    lines.push({
      reason: 'ancestor_hidden_nav',
      label: INHERITED_STATUS_LABELS.ancestor_hidden_nav,
      markColor: INHERITED_STATUS_COLORS.ancestor_hidden_nav,
    })
  }

  if (node.isActive !== true) {
    lines.push({
      reason: 'self_inactive',
      label: INHERITED_STATUS_LABELS.self_inactive,
      markColor: INHERITED_STATUS_COLORS.self_inactive,
    })
  }

  if (node.showInNavigation !== true) {
    lines.push({
      reason: 'self_hidden_nav',
      label: INHERITED_STATUS_LABELS.self_hidden_nav,
      markColor: INHERITED_STATUS_COLORS.self_hidden_nav,
    })
  }

  return lines
}

/**
 * Create-child policy for a parent node (uses parent's own flags + its ancestors).
 */
export function getCreateChildPolicy(parentNode, parentAncestors = []) {
  if (!parentNode) {
    return { allowed: false, code: 'no_parent', message: 'Оберіть батьківський рівень.' }
  }

  const { effectiveArchived } = computeEffectiveState(parentNode, parentAncestors)

  if (effectiveArchived || parentNode.isArchived === true) {
    return {
      allowed: false,
      code: 'parent_archived',
      message: 'Спочатку відновіть батьківський вузол з архіву.',
    }
  }

  const parentActive = parentNode.isActive === true
  const ancestorsActive = parentAncestors.every((a) => a?.isActive === true)
  if (!parentActive || !ancestorsActive) {
    return {
      allowed: true,
      code: 'parent_inactive',
      warning:
        'Батьківський вузол неактивний: новий елемент буде недоступний (effective), доки батька не активують.',
    }
  }

  return { allowed: true, code: 'ok', warning: null }
}

export function flagsForArchiveWrite(previousNode = {}) {
  return {
    isArchived: true,
    isActive: false,
    // preserve navigation intent
    showInNavigation: previousNode.showInNavigation === true,
  }
}
